import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from './http';

/**
 * Query defaults: no retry for 4xx (business errors are final). The server being briefly unreachable (network error,
 * 502/503/504 — e.g. while a new version starts after a deploy) is retried for ~25s; other 5xx up to 2 times.
 * Mutations show a toast on failure unless they set `meta: { silent: true }` (forms that map field errors inline).
 */
/** Server unreachable or restarting: worth waiting for rather than showing an error. */
export function isTransient(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504;
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < (isTransient(error) ? 5 : 2);
}

/** 1s, 2s, 4s, 8s, 8s … (≈23s in total over 5 retries). */
export function queryRetryDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 8000);
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: undefined,
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        if (mutation.meta?.silent) return;
        if (error instanceof ApiError && (error.isValidation || error.isConflict)) return; // shown inline
        toast.error(error instanceof ApiError ? error.detail : 'Thao tác không thành công.', {
          description: error instanceof ApiError && error.supportTraceId ? `Mã hỗ trợ: ${error.supportTraceId}` : undefined,
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        retry: shouldRetryQuery,
        retryDelay: queryRetryDelay,
      },
      mutations: { retry: false },
    },
  });
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { silent?: boolean };
  }
}
